/**
 * @Module: Navigation Component
 * @Status: Ready
 * @Summary: Responsive nav with active states, icons, and theme picker
 */

import React from 'react';
import {
	Box,
	Flex,
	Button,
	HStack,
	VStack,
	Text,
	Badge,
	IconButton,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	DrawerBody,
	DrawerHeader,
	Divider,
	useDisclosure,
	useColorMode,
} from '@chakra-ui/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useShoppingList } from '../../contexts/ShoppingListContext';
import { AppIcon } from '../icons/AppIcon';
import type { IconName } from '../icons/AppIcon';

const NAV_LINKS: { to: string; label: string; icon: IconName; authOnly?: boolean }[] = [
	{ to: '/browse',    label: 'Browse',     icon: 'ui.chef' },
	{ to: '/meal-plan', label: 'Meal Plan',  icon: 'ui.notes' },
	{ to: '/generate',  label: 'Generate',   icon: 'ui.wand' },
	{ to: '/import',    label: 'Import',     icon: 'ui.download' },
	{ to: '/kitchen',   label: 'Kitchen',    icon: 'ui.kitchen' },
	{ to: '/pantry',    label: 'Pantry',     icon: 'shop.pantry' },
	{ to: '/shopping',  label: 'Shopping',   icon: 'ui.shoppingCart' },
	{ to: '/log',       label: 'Food Log',   icon: 'ui.calendar' },
	{ to: '/progress',  label: 'Progress',   icon: 'ui.trophy' },
	{ to: '/preferences', label: 'Preferences', icon: 'ui.edit', authOnly: true },
];

const Navigation: React.FC = () => {
	const { user, signOut } = useAuth();
	const { items: shoppingItems } = useShoppingList();
	const shoppingCount = shoppingItems.filter(i => !i.isChecked).length;
	const navigate = useNavigate();
	const location = useLocation();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { colorMode, toggleColorMode } = useColorMode();

	const handleLogout = async () => {
		onClose();
		await signOut();
		navigate('/browse');
	};

	const visibleLinks = NAV_LINKS.filter((l) => !l.authOnly || user);
	const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

	return (
		<Box
			bg="surface.nav"
			px={4}
			borderBottom="1px solid"
			borderColor="border.subtle"
			position="sticky"
			top={0}
			zIndex="sticky"
			backdropFilter="blur(12px)"
			sx={{
				backgroundColor: colorMode === 'light'
					? 'rgba(255, 255, 255, 0.85)'
					: 'rgba(30, 26, 21, 0.85)',
			}}
		>
			<Flex h={16} alignItems="center" justifyContent="space-between" maxW="1200px" mx="auto">
				<Link to="/browse">
					<HStack spacing={2} _hover={{ opacity: 0.8 }} transition="opacity 200ms">
						<img src="/logo.svg" alt="Staple It" width={28} height={28} />
						<Text fontSize="xl" fontWeight="bold" fontFamily="heading" color="text.primary">
							Staple It
						</Text>
					</HStack>
				</Link>

				<HStack spacing={1} display={{ base: 'none', md: 'flex' }}>
					{visibleLinks.map((link) => (
						<Link key={link.to} to={link.to}>
							<Button
								variant="ghost"
								size="sm"
								color={isActive(link.to) ? 'sage.600' : 'text.secondary'}
								fontWeight={isActive(link.to) ? 600 : 500}
								borderBottom="2px solid"
								borderColor={isActive(link.to) ? 'sage.500' : 'transparent'}
								borderRadius="0"
								px={3}
								h={16}
								fontSize="sm"
								transition="all 200ms cubic-bezier(0.16, 1, 0.3, 1)"
								_hover={{ color: 'sage.600', bg: 'surface.hover' }}
								leftIcon={<AppIcon name={link.icon} size={14} />}
							>
								{link.label}
								{link.to === '/shopping' && shoppingCount > 0 && (
									<Badge ml={1} colorScheme="terracotta" borderRadius="full" fontSize="2xs" minW="16px" textAlign="center">
										{shoppingCount}
									</Badge>
								)}
							</Button>
						</Link>
					))}
				</HStack>

				<Flex alignItems="center" display={{ base: 'none', md: 'flex' }} gap={3}>
					<IconButton
						aria-label="Toggle theme"
						icon={<AppIcon name={colorMode === 'light' ? 'ui.moon' : 'ui.sun'} size={16} />}
						variant="ghost"
						size="sm"
						borderRadius="lg"
						onClick={toggleColorMode}
					/>
					{user ? (
						<HStack spacing={3}>
							<Text color="text.muted" fontSize="xs" maxW="120px" noOfLines={1}>
								{user.displayName || user.email}
							</Text>
							<Button
								variant="kitchen-outline"
								size="sm"
								onClick={handleLogout}
								leftIcon={<AppIcon name="ui.logout" size={14} />}
							>
								Sign Out
							</Button>
						</HStack>
					) : (
						<Link to="/login">
							<Button
								variant="kitchen-outline"
								size="sm"
								leftIcon={<AppIcon name="ui.login" size={14} />}
							>
								Sign In
							</Button>
						</Link>
					)}
				</Flex>

				<IconButton
					aria-label="Open menu"
					icon={<AppIcon name="ui.menu" size={20} />}
					variant="ghost"
					display={{ base: 'flex', md: 'none' }}
					onClick={onOpen}
				/>
			</Flex>

			<Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
				<DrawerOverlay />
				<DrawerContent bg="surface.card">
					<DrawerCloseButton />
					<DrawerHeader borderBottomWidth="1px" borderColor="border.subtle">
						<HStack spacing={2}>
							<img src="/logo.svg" alt="Staple It" width={24} height={24} />
							<Text fontFamily="heading" fontSize="lg" color="text.primary">
								Staple It
							</Text>
						</HStack>
					</DrawerHeader>

					<DrawerBody pt={4}>
						<VStack spacing={1} align="stretch">
							{visibleLinks.map((link) => (
								<Link key={link.to} to={link.to} onClick={onClose}>
									<Button
										variant="ghost"
										size="md"
										w="100%"
										justifyContent="flex-start"
										borderLeft="3px solid"
										borderColor={isActive(link.to) ? 'sage.500' : 'transparent'}
										bg={isActive(link.to) ? 'surface.active' : 'transparent'}
										color={isActive(link.to) ? 'sage.600' : 'text.primary'}
										fontWeight={isActive(link.to) ? 600 : 500}
										borderRadius="0"
										py={3}
										transition="all 200ms cubic-bezier(0.16, 1, 0.3, 1)"
										_hover={{ bg: 'surface.hover' }}
										leftIcon={<AppIcon name={link.icon} size={16} />}
									>
										{link.label}
									</Button>
								</Link>
							))}

							<Divider borderColor="border.subtle" my={2} />

							<Button
								variant="ghost"
								size="md"
								w="100%"
								justifyContent="flex-start"
								py={3}
								onClick={toggleColorMode}
								leftIcon={<AppIcon name={colorMode === 'light' ? 'ui.moon' : 'ui.sun'} size={16} />}
							>
								{colorMode === 'light' ? 'Dark Mode' : 'Light Mode'}
							</Button>

							<Divider borderColor="border.subtle" my={2} />

							{user ? (
								<VStack spacing={2} align="stretch">
									<Text color="text.muted" fontSize="xs" px={3}>
										{user.displayName || user.email}
									</Text>
									<Button
										variant="kitchen-outline"
										size="sm"
										onClick={handleLogout}
										leftIcon={<AppIcon name="ui.logout" size={14} />}
									>
										Sign Out
									</Button>
								</VStack>
							) : (
								<Link to="/login" onClick={onClose}>
									<Button
										variant="kitchen-outline"
										size="sm"
										w="100%"
										leftIcon={<AppIcon name="ui.login" size={14} />}
									>
										Sign In
									</Button>
								</Link>
							)}
						</VStack>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</Box>
	);
};

export default Navigation;
